export interface ITableProps {
  children?: React.ReactNode;
  onClick?: () => void;
  styles?: string;
  className?: string;
}

// ONE ROW SHAPE FOR THE LICENCE TABLE, whichever server issued it.
//
// The page used to render Squarebit-issued licences in a layout of their own,
// beside a table of LicenseSpring ones - so a customer holding both met two
// different presentations of the same idea, and only one of them told them
// their seat count at a glance. The table is the presentation; `backend` says
// who to ask when a control is used.
export type LicenseBackend = 'spring' | 'squarebit';

export interface Ikey {
  productName: string;
  key: string;
  type: 'trial' | 'monthly' | 'annual' | 'perpetual' | 'free';
  expiresAt: string;
  maxActivations: number;
  usedActivations: number;
  //: Which licence server owns this row. Absent means LicenseSpring, so every
  //: existing caller keeps working untouched; Squarebit-issued rows say so.
  backend?: LicenseBackend;
  //: squarebit backend only - what its controls need.
  licenseId?: number;
  productSlug?: string;
  activations?: ILicenseActivation[];
  source?: string;
  status?: string;
  // Mirrors Stripe subscription state; null for trial licenses with no Stripe sub.
  // active | trialing | past_due | paused | unpaid | incomplete | incomplete_expired | canceled
  subscriptionStatus: string | null;
  cancelAtPeriodEnd: boolean;
  stripeId: string | null;
  springId: string;
}

// Squarebit-issued licence -> a table row. The conversions that matter:
//
//   expiresAt null  a Squarebit perpetual licence has no date; the table
//                   shows the word rather than an empty cell.
//   type            the table badges a BILLING type. Squarebit licences carry
//                   a SOURCE instead (free / stripe / comped), which is the
//                   same question - what kind of licence is this - so it is
//                   what the badge shows.
//: Slug -> the words a customer bought it under. Eye is the only Squarebit-
//: issued product today; the fallback title-cases the slug so the next one
//: reads sensibly on the day it ships rather than on the day someone
//: remembers to add it here.
const PRODUCT_NAMES: Record<string, string> = {
  'squarebit-eye': 'Squarebit Eye'
};

const productNameFor = (slug: string) =>
  PRODUCT_NAMES[slug] ??
  slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const productLicenseToRow = (license: IProductLicense): Ikey => ({
  productName: productNameFor(license.productSlug),
  key: license.key,
  type: (license.source as Ikey['type']) ?? 'perpetual',
  expiresAt: license.expiresAt ?? '',
  maxActivations: license.seats,
  usedActivations: license.usedSeats,
  subscriptionStatus: null,
  cancelAtPeriodEnd: false,
  stripeId: null,
  springId: '',
  backend: 'squarebit',
  licenseId: license.id,
  productSlug: license.productSlug,
  activations: license.activations,
  source: license.source,
  status: license.status
});

// A machine currently holding a seat on a Squarebit-issued licence. The server
// never sends anything that identifies the hardware — `machineLabel` is a
// hostname the user typed or the plugin read, and the actual machine binding is
// a keyed hash that stays server-side.
export interface ILicenseActivation {
  id: number;
  machineLabel: string | null;
  os: string | null;
  hostApp: string | null;
  appVersion: string | null;
  seatClass: string;
  activatedAt: string;
  lastSeenAt: string;
  releasedAt: string | null;
  // online | offline. Display only — an offline seat counts identically.
  activationMode: string;
  // When the signed .lic stops verifying. Offline rows only.
  tokenExpiresAt: string | null;
}

// A licence from Squarebit's own licence server. Deliberately NOT `Ikey`: that
// type mirrors a LicenseSpring row and shares no fields with this one beyond
// the word "key".
export interface IProductLicense {
  id: number;
  productSlug: string;
  key: string;
  seats: number;
  usedSeats: number;
  // active | suspended | revoked | lapsed
  status: string;
  statusReason: string | null;
  source: string;
  // null = perpetual
  expiresAt: string | null;
  createdAt: string;
  activations: ILicenseActivation[];
}

// What the server returns after an offline activation: the signed .lic
// contents plus a suggested filename to save it as.
export interface IOfflineActivationResult {
  activationId: number;
  productSlug: string;
  seats: number;
  usedSeats: number;
  token?: string;
  tokenExpiresAt?: string;
  filename: string;
}

export interface IFileType {
  mac: string;
  linux: string;
  windows: string;
}

export interface IFileResponse {
  macfile: Blob;
  linuxfile: Blob;
  windowsfile: Blob;
}
